import type { AxiosError } from 'axios';
import axios from 'axios';

import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import type { StatusKey } from '../../../enum';
import type { ScoreDetail } from '../../../models/client';
import type { ErrorMessage } from '../../../models/error';
import type { AsyncThunkConfig } from '../../../models/slice';
import { RaygunErrorHandlerService } from '../../../service/raygun.service';

const { logError } = RaygunErrorHandlerService();

type ClientProfileIndicatorGraph = {
  recent_indicators: RecentIndicator[];
};

type ClientProfileMoods = {
  recent_moods: RecentMoods[];
};

type RecentMoods = {
  count: number;
  mood: string;
  mood_id: string;
};

export type ClientLocation = {
  id: string;
  client_id: string;
  speed: number;
  latitude: number;
  longitude: number;
  bearing: number | null;
  location_type: string;
  accuracy: number;
  heading: number;
  altitude: number;
  altitude_accuracy: number;
  time: number;
  organization_id: string;
  created: string;
  submission_id: string;
};

export class Priority {
  id: string = '';
  full_name: string = '';
  number: number | null = null;
  level: string = '';
  meaning: string = '';
  order: number | null = null;
  text_colour: string = '';
  fill_colour: string = '';
}

export class CaseStatus {
  id: string = '';
  name: string = '';
  description: string = '';
  order: number | null = null;
  text_colour: string = '';
  fill_colour: string = '';
}

export class ClientProfileInfo {
  id: string = '';
  medical_record_number: string = '';
  organization_name: string = '';
  sites: string[] = [];
  status: StatusKey | null = null;
  priority_id: string | null = null;
  case_status_id: string | null = null;
  follow_up_by: string | null = null;
  mobile_phone: string = '';
  client_last_name: string = '';
  client_first_name: string = '';
  email: string = '';
  address: string = '';
  city: string = '';
  state: string = '';
  postalcode: string = '';
  account_status: string = '';
}
export class ClientLastSentiment {
  status: StatusKey | null = null;
  sentiment: number | null = null;
  created_date: string | null = null;
}

export type OLHCheckIn = {
  submission_id: string;
  measurement_created: string;
  site_name: string | null;
  systolic_blood_pressure: number | null;
  diastolic_blood_pressure: number | null;
  pulse: number | null;
  weight_in_kg: number | null;
  height_in_cm: number | null;
  bmi: number | null;
};

export type ClientProfileSpiderChartInfo = {
  client_journal_id: string;
  product_short_name: string;
  assigned_journal_id: string;
  id: string;
  created_date: string;
  diary: string;
  status: string;
  fields: Fields[];
  max_field_value: number;
  min_field_value: number;
  account_status: string;
  custom_fields: object | null;
};

type Fields = {
  hide_in_radial: boolean;
  name: string;
  value: number;
};

export type RecentIndicator = {
  type: string;
  data: {
    count: number;
    response: string;
    time: string;
  }[];
  max: number;
  min: number;
};

export type APAClientIndicator = {
  product: string;
  client_journal_id: string;
  created_date: string;
  status: string;
  score: number;
  score_details: ScoreDetail;
  last_cravings: number;
  fields: APAField[];
};

type APAField = {
  name: string;
  value: string;
  status: string;
  img: string;
  numeric_value: number;
  product_field_id: string;
};

export type InterventionDetails = {
  client_id: string | null;
  method: string | null;
  details: string | null;
};

export type RadarIndicatorData = {
  name: string;
  min: number;
  max: number;
};

export class RadarData {
  name: string | undefined = undefined;
  value: number[] | undefined = undefined;
}

export type TabKey = 'submissions' | 'entries' | 'badges' | 'graphs' | 'maps' | 'settings';

type ClientProfileSlideType = {
  activeTab: TabKey;
  clientProfileIndicatorGraph: ClientProfileIndicatorGraph;
  clientProfileSpiderInfo: ClientProfileSpiderChartInfo[];
  clientProfileMoods: ClientProfileMoods;
  clientProfileInfo: ClientProfileInfo;
  loadingClientProfileInfo: boolean;
  clientLastSentiment: ClientLastSentiment;
  loadingClientLastSentiment: boolean;
  lastCheckIn: OLHCheckIn | null;
  loadingLastCheckIn: boolean;
  apaClientIndicators: APAClientIndicator[];
  loadingClientRiskIndicator: boolean;
  loadingClientMoodIndicator: boolean;
  loadingClientSpiderIndicator: boolean;
  loadingApaClientIndicator: boolean;
  hasDevice: boolean;
  dateRange: string;
  chartStyle: string;
  interventionDetails: InterventionDetails;
  loadingClientIntervention: boolean;
  Priorities: Priority[];
  CaseStatuses: CaseStatus[];
};

const initialState: ClientProfileSlideType = {
  activeTab: 'submissions',
  clientProfileIndicatorGraph: {
    recent_indicators: [],
  },
  clientProfileMoods: {
    recent_moods: [],
  },
  clientProfileSpiderInfo: [],
  apaClientIndicators: [],
  clientProfileInfo: new ClientProfileInfo(),
  loadingClientProfileInfo: false,
  clientLastSentiment: new ClientLastSentiment(),
  loadingClientLastSentiment: false,
  lastCheckIn: null,
  loadingLastCheckIn: false,
  loadingClientRiskIndicator: false,
  loadingClientMoodIndicator: false,
  loadingClientSpiderIndicator: false,
  loadingApaClientIndicator: false,
  hasDevice: false,
  dateRange: '2 weeks',
  chartStyle: 'lineChart',
  interventionDetails: { client_id: null, method: null, details: null },
  loadingClientIntervention: false,
  Priorities: [],
  CaseStatuses: [],
};

export const fetchClientProfileGraphs = createAsyncThunk<ClientProfileIndicatorGraph, string, AsyncThunkConfig>(
  'clientProfile/fetchClientProfileGraphs',
  async (client_id, thunkAPI) => {
    try {
      const interval = thunkAPI.getState().clientProfileSlice.dateRange;
      const response = (await axios.post(
        'v3_client_profile_recent_indicators',
        { client_id, interval },
        { signal: thunkAPI.signal },
      )) as ClientProfileIndicatorGraph;
      return response ?? initialState.clientProfileIndicatorGraph;
    } catch (e) {
      logError(e, ['clientProfileSlice', 'fetchClientProfileGraphs']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const fetchClientProfileMoods = createAsyncThunk<ClientProfileMoods, string, AsyncThunkConfig>(
  'clientProfile/fetchClientProfileMoods',
  async (client_id, thunkAPI) => {
    try {
      const response = (await axios.post(
        'v3_client_profile_recent_moods',
        { client_id, interval: '6 weeks' },
        { signal: thunkAPI.signal },
      )) as ClientProfileMoods;
      return response ?? initialState.clientProfileMoods;
    } catch (e) {
      logError(e, ['clientProfileSlice', 'fetchClientProfileMoods']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const fetchClientProfileInfo = createAsyncThunk<ClientProfileInfo, string, AsyncThunkConfig>(
  'clientProfile/fetchClientProfileInfo',
  async (client_id, thunkAPI) => {
    try {
      const response = (await axios.post(
        'v2_client_profile',
        { client_id },
        { signal: thunkAPI.signal },
      )) as ClientProfileInfo[];
      return response[0] ?? initialState.clientProfileInfo;
    } catch (e) {
      logError(e, ['clientProfileSlice', 'fetchClientProfileInfo']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const fetchClientLastSentiment = createAsyncThunk<ClientLastSentiment, string, AsyncThunkConfig>(
  'clientProfile/fetchClientLastSentiment',
  async (client_id, thunkAPI) => {
    try {
      const response = (await axios.post(
        'v3_client_profile_last_sentiment',
        { client_id },
        { signal: thunkAPI.signal },
      )) as ClientLastSentiment;
      return { ...initialState.clientLastSentiment, ...response };
    } catch (e) {
      logError(e, ['clientProfileSlice', 'fetchClientLastSentiment']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const fetchClientLastCheckIn = createAsyncThunk<OLHCheckIn | null, string, AsyncThunkConfig>(
  'clientProfile/fetchClientLastCheckIn',
  async (client_id, thunkAPI) => {
    try {
      const response = (await axios.post(
        'v3_olh_client_last_check_in',
        { client_id },
        { signal: thunkAPI.signal },
      )) as OLHCheckIn[];
      return response?.[0] ?? null;
    } catch (e) {
      logError(e, ['clientProfileSlice', 'fetchClientLastCheckIn']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const fetchClientPriorities = createAsyncThunk<Priority[], undefined, AsyncThunkConfig>(
  'clientProfile/fetchClientPriorities',
  async (_, thunkAPI) => {
    try {
      const response = (await axios.post('v0_priorities', {}, { signal: thunkAPI.signal })) as Priority[];
      return response ?? [];
    } catch (e) {
      logError(e, ['clientProfileSlice', 'fetchClientPriorities']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const fetchClientCaseStatuses = createAsyncThunk<CaseStatus[], undefined, AsyncThunkConfig>(
  'clientProfile/fetchClientCaseStatuses',
  async (_, thunkAPI) => {
    try {
      const response = (await axios.post('v0_case_statuses', {}, { signal: thunkAPI.signal })) as CaseStatus[];
      return response ?? [];
    } catch (e) {
      logError(e, ['clientProfileSlice', 'fetchClientCaseStatuses']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const fetchClientSpiderChartInfo = createAsyncThunk<ClientProfileSpiderChartInfo[], string, AsyncThunkConfig>(
  'clientProfile/fetchClientSpiderChartInfo',
  async (client_id, thunkAPI) => {
    try {
      const response = (await axios.post(
        'v3_client_spider_indicator_info',
        { client_id },
        { signal: thunkAPI.signal },
      )) as ClientProfileSpiderChartInfo[];
      return response ?? [];
    } catch (e) {
      logError(e, ['clientProfileSlice', 'fetchClientSpiderChartInfo']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const fetchAPAClientProfileGraph = createAsyncThunk<APAClientIndicator[], string, AsyncThunkConfig>(
  'clientProfile/fetchAPAClientProfileGraph',
  async (client_id, thunkAPI) => {
    try {
      const response = (await axios.post(
        'v2_client_profile_apa',
        { client_id },
        { signal: thunkAPI.signal },
      )) as APAClientIndicator[];
      return response ?? [];
    } catch (e) {
      logError(e, ['clientProfileSlice', 'fetchAPAClientProfileGraph']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const fetchDeviceStatus = createAsyncThunk<boolean, string, AsyncThunkConfig>(
  'clientProfile/fetchDeviceStatus',
  async (client_id, thunkAPI) => {
    try {
      const response = (await axios.post(
        'v3_client_valid_device_status',
        { client_id },
        { signal: thunkAPI.signal },
      )) as boolean;
      return response;
    } catch (e) {
      logError(e, ['clientProfileSlice', 'fetchDeviceStatus']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const postIntervention = createAsyncThunk<string, InterventionDetails, AsyncThunkConfig>(
  'clientProfile/postIntervention',
  async (interventionDetails, thunkAPI) => {
    try {
      const response = (await axios.post('v2_intervention_record', interventionDetails)) as string;
      if (response && interventionDetails.details) {
        window.location.href = interventionDetails.details;
      }
      return response; // client_id
    } catch (e) {
      logError(e, ['clientProfileSlice', 'postIntervention']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const clientProfileSlice = createSlice({
  name: 'clientProfile',
  initialState,
  reducers: {
    setActiveTab: (state, action: PayloadAction<TabKey>) => {
      state.activeTab = action.payload;
    },
    setDateRange: (state, action: PayloadAction<string>) => {
      state.dateRange = action.payload;
    },
    setChartStyle: (state, action: PayloadAction<string>) => {
      state.chartStyle = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDeviceStatus.fulfilled, (state, action) => {
        state.hasDevice = action.payload;
      })
      .addCase(fetchClientProfileGraphs.pending, (state, _action) => {
        state.loadingClientRiskIndicator = true;
      })
      .addCase(fetchClientProfileGraphs.fulfilled, (state, action) => {
        state.clientProfileIndicatorGraph = action.payload;
        state.loadingClientRiskIndicator = false;
      })
      .addCase(fetchClientProfileGraphs.rejected, (state, action) => {
        if (action.error.message !== 'Aborted') {
          state.loadingClientRiskIndicator = false;
        }
      })
      .addCase(fetchClientProfileMoods.pending, (state, _action) => {
        state.loadingClientMoodIndicator = true;
      })
      .addCase(fetchClientProfileMoods.fulfilled, (state, action) => {
        state.clientProfileMoods = action.payload;
        state.loadingClientMoodIndicator = false;
      })
      .addCase(fetchClientProfileMoods.rejected, (state, action) => {
        if (action.error.message !== 'Aborted') {
          state.loadingClientMoodIndicator = false;
        }
      })
      .addCase(fetchClientProfileInfo.pending, (state, _action) => {
        state.loadingClientProfileInfo = true;
      })
      .addCase(fetchClientProfileInfo.fulfilled, (state, action) => {
        state.clientProfileInfo = action.payload;
        state.loadingClientProfileInfo = false;
      })
      .addCase(fetchClientProfileInfo.rejected, (state, action) => {
        if (action.error.message !== 'Aborted') {
          state.loadingClientProfileInfo = false;
        }
      })
      .addCase(fetchClientLastSentiment.pending, (state, _action) => {
        state.loadingClientLastSentiment = true;
      })
      .addCase(fetchClientLastSentiment.fulfilled, (state, action) => {
        state.clientLastSentiment = action.payload;
        state.loadingClientLastSentiment = false;
      })
      .addCase(fetchClientLastSentiment.rejected, (state, action) => {
        if (action.error.message !== 'Aborted') {
          state.loadingClientLastSentiment = false;
        }
      })
      .addCase(fetchClientLastCheckIn.pending, (state, _action) => {
        state.loadingLastCheckIn = true;
      })
      .addCase(fetchClientLastCheckIn.fulfilled, (state, action) => {
        state.lastCheckIn = action.payload;
        state.loadingLastCheckIn = false;
      })
      .addCase(fetchClientLastCheckIn.rejected, (state, action) => {
        if (action.error.message !== 'Aborted') {
          state.loadingLastCheckIn = false;
        }
      })
      .addCase(fetchClientSpiderChartInfo.pending, (state, _action) => {
        state.loadingClientSpiderIndicator = true;
      })
      .addCase(fetchClientSpiderChartInfo.fulfilled, (state, action) => {
        state.clientProfileSpiderInfo = action.payload;
        state.loadingClientSpiderIndicator = false;
      })
      .addCase(fetchClientSpiderChartInfo.rejected, (state, action) => {
        if (action.error.message !== 'Aborted') {
          state.loadingClientSpiderIndicator = false;
        }
      })
      .addCase(fetchAPAClientProfileGraph.pending, (state, _action) => {
        state.loadingApaClientIndicator = true;
      })
      .addCase(fetchAPAClientProfileGraph.fulfilled, (state, action) => {
        state.apaClientIndicators = action.payload;
        state.loadingApaClientIndicator = false;
      })
      .addCase(fetchAPAClientProfileGraph.rejected, (state, action) => {
        if (action.error.message !== 'Aborted') {
          state.loadingApaClientIndicator = false;
        }
      })
      .addCase(postIntervention.pending, (state) => {
        state.loadingClientIntervention = true;
      })
      .addCase(postIntervention.fulfilled, (state) => {
        state.loadingClientIntervention = false;
      })
      .addCase(postIntervention.rejected, (state, _action) => {
        state.loadingClientIntervention = false;
      })
      .addCase(fetchClientPriorities.fulfilled, (state, action) => {
        state.Priorities = action.payload;
      })
      .addCase(fetchClientCaseStatuses.fulfilled, (state, action) => {
        state.CaseStatuses = action.payload;
      });
  },
});

export const { setActiveTab, setDateRange, setChartStyle } = clientProfileSlice.actions;
